/**
 * Sanity, health, inventory, pickups and interaction.
 *
 * Nothing here runs on a clock the player has to feed. The mind drains on what
 * the room is doing and the body only loses ground when something takes it,
 * so most of a run is just walking — which is the point. This system owns every
 * stat the player has.
 */

import { TUNING } from '../core/config';
import { clamp, clamp01, damp, lerp, smoothstep } from '../core/math';
import { makeRNG } from '../core/rng';
import type { SurvivalSystem } from '../core/systems';
import type {
  EventBus,
  GameContext,
  GameEvents,
  GeneratedLevel,
  Interactable,
  InventorySlot,
  ItemKind,
  ItemSpot,
  PlayerState,
  RNG,
  StatKey,
  SurvivalStats,
} from '../core/types';

import { Inventory } from './inventory';
import { FocusFinder } from './interaction';
import { hasUsefulEffect, itemDef } from './items';
import { NOTES, RULES_NOTE_ID, pickWhisper } from './notes';
import { darknessDrain, effectiveLight, entityPressure, litRecovery } from './sanity';

/** Seconds between throttled `stats:changed` emissions during ordinary drain. */
const STATS_INTERVAL = 0.25;
/** Points a stat must move before the throttled emission bothers. */
const STATS_EPSILON = 0.5;
/** Stat must climb this far back above the critical line before it can re-fire. */
const CRITICAL_HYSTERESIS = 4;

/**
 * Seconds a reported `entity:dread` rate stays live. Behaviours tick at 20Hz,
 * so anything older than a few ticks means the entity stopped reporting and the
 * pressure is over.
 */
const DREAD_TTL = 0.25;

/** Sanity below this and the level starts talking. */
const WHISPER_SANITY = 35;
const WHISPER_INTERVAL_HIGH = 34;
/**
 * There are seven whisper lines. At a nine-second floor a bottomed-out bar
 * repeated all of them every couple of minutes, which is chatty rather than
 * oppressive.
 */
const WHISPER_INTERVAL_LOW = 20;
const WHISPER_MS = 3400;

/** Speed multiplier at zero health. Injury is the only thing that slows you. */
const INJURED_SPEED_SCALE = 0.85;
/** Health at or above this leaves the player unimpaired. */
const INJURY_CEILING = 30;

/** Stats this system watches for the critical threshold. */
const WATCHED: readonly StatKey[] = ['health', 'sanity'];

type HurtSource = GameEvents['player:hurt']['source'];
const HURT_SOURCES = new Set<string>([
  'hound',
  'smiler',
  'bacteria',
  'faceling',
  'deathmoth',
  'partygoer',
  'centipede',
  'clump',
  'hollow',
  'fall',
]);

class Survival implements SurvivalSystem {
  readonly stats: SurvivalStats = { health: 100, sanity: 100 };
  focused: Interactable | null = null;

  private readonly inv = new Inventory(TUNING.survival.inventorySlots);
  private readonly items: ItemSpot[] = [];
  /** Interactables mirroring `items`, kept in step so focus stays allocation-free. */
  private readonly itemActs: Interactable[] = [];
  private readonly extras: Interactable[] = [];
  /** `itemActs` and `extras` in one list. Emptied and refilled, never rebuilt. */
  private readonly joined: Interactable[] = [];
  private readonly focusFinder = new FocusFinder();
  /** True while a pointer is doing the selecting. See `setAiming`. */
  private aiming = false;
  private readonly unsubscribe: Array<() => void> = [];

  private level: GeneratedLevel | null = null;
  private player: PlayerState | null = null;
  private rng: RNG = makeRNG(1);
  /** Which note each note-spot on this level holds, so re-reads never change. */
  private readonly noteFor = new Map<number, number>();

  private clock = 0;
  private dirty = true;
  private lastEmit = -1;
  private readonly sent: SurvivalStats = { health: 0, sanity: 0 };
  private readonly belowCritical = new Set<StatKey>();

  /** Seconds since the last hit. The only gate on health coming back. */
  private sinceDamage = TUNING.survival.regenDelay;

  private whisperTimer = WHISPER_INTERVAL_HIGH;
  /** Latest sanity pressure reported by each live entity, points/second. */
  private readonly dread = new Map<number, { rate: number; at: number }>();
  private speedScale = 1;
  private promptFor: Interactable | null = null;
  private died = false;
  /** Vets pickups when a shared world is in play. See `setPickupGate`. */
  private gate: ((spot: ItemSpot) => boolean) | null = null;

  constructor(private readonly events: EventBus) {
    // A run restart reaches us through the bus; there is no other signal that
    // distinguishes "new run" from "next level".
    this.unsubscribe.push(this.events.on('game:start', () => this.resetRun()));
    // Nothing else applies entity damage to the player, and entities cannot
    // call us directly, so the attack event is where the hit lands.
    this.unsubscribe.push(
      this.events.on('entity:attack', ({ kind, damage }) => {
        if (damage > 0) this.damage(damage, kind);
      }),
    );
    // Behaviour-specific dread (a smiler being stared at) arrives as a rate. It
    // is folded into the generic proximity model with a max rather than a sum,
    // so the two descriptions of the same monster never stack.
    this.unsubscribe.push(
      this.events.on('entity:dread', ({ id, rate }) => {
        if (rate > 0) this.dread.set(id, { rate, at: this.clock });
      }),
    );
    this.unsubscribe.push(this.events.on('entity:despawned', ({ id }) => this.dread.delete(id)));
  }

  get inventory(): readonly InventorySlot[] {
    return this.inv.slots;
  }

  get worldItems(): readonly ItemSpot[] {
    return this.items;
  }

  get interactables(): readonly Interactable[] {
    const out = this.joined;
    out.length = 0;
    for (let i = 0; i < this.itemActs.length; i++) out.push(this.itemActs[i]!);
    for (let i = 0; i < this.extras.length; i++) out.push(this.extras[i]!);
    return out;
  }

  /* ---------------------------------------------------------------- *
   * Level lifecycle
   * ---------------------------------------------------------------- */

  setLevel(level: GeneratedLevel, levelIndex: number): void {
    this.level = level;
    this.rng = makeRNG(level.seed ^ 0x51a9e);

    // Deep copy: the generator shares Vec2 instances between items, lights and
    // exits, so aliasing a position here would move a light fixture.
    this.items.length = 0;
    for (const spot of level.items) {
      this.items.push({ id: spot.id, pos: { x: spot.pos.x, z: spot.pos.z }, kind: spot.kind, amount: spot.amount });
    }

    this.noteFor.clear();
    const deck = this.rng.shuffle(NOTES.map((_, i) => i));
    // The rules note is the only one that is instruction rather than colour, and
    // a player who meets their first hound without it has been told nothing. On
    // Level 0 it is dealt first — `placeItems` puts guaranteed items on the
    // scored route, so the first note spot is one the player walks past — and
    // cut out of the deck so it cannot also turn up again later.
    if (levelIndex === 0) {
      const rules = NOTES.findIndex((n) => n.id === RULES_NOTE_ID);
      if (rules >= 0) {
        const at = deck.indexOf(rules);
        if (at > 0) {
          deck[at] = deck[0]!;
          deck[0] = rules;
        }
      }
    }
    let cursor = 0;
    for (const spot of this.items) {
      if (spot.kind === 'note') this.noteFor.set(spot.id, deck[cursor++ % deck.length]!);
    }

    this.itemActs.length = 0;
    for (const spot of this.items) this.itemActs.push(this.makeItemAct(spot));

    this.extras.length = 0;
    this.setFocus(null);
    // `EntitySystem.clear()` is a teardown, not six things leaving the room, so
    // no despawn events arrive to retire these.
    this.dread.clear();
    // A hit taken on the way out of the last floor should not hold healing off
    // on the new one: arriving somewhere is a fresh start for the body.
    this.sinceDamage = TUNING.survival.regenDelay;

    // Descending must not heal you: stats and inventory only reset at depth 0.
    if (levelIndex === 0) this.resetRun();
  }

  private resetRun(): void {
    const s = this.stats;
    s.health = 100;
    s.sanity = 100;
    this.inv.clear();
    this.belowCritical.clear();
    this.died = false;
    this.speedScale = 1;
    this.whisperTimer = WHISPER_INTERVAL_HIGH;
    this.sinceDamage = TUNING.survival.regenDelay;
    this.events.emit('inventory:changed', { slots: this.inv.slots });
    this.markChanged();
    this.flush(true);
  }

  /* ---------------------------------------------------------------- *
   * Frame
   * ---------------------------------------------------------------- */

  update(ctx: GameContext): void {
    this.player = ctx.player;
    this.clock += ctx.dt;

    this.updateBody(ctx);
    this.updateSanity(ctx);
    this.updateSpeedScale(ctx.dt, ctx.player);
    this.updateFocus(ctx);
    this.checkCritical();
    this.flush(false);
  }

  /**
   * Health, which only ever moves for a reason. It comes back once nothing has
   * hit the player for a while — the reward is for getting away and staying
   * away, which is the thing the game wants them to learn to do.
   */
  private updateBody(ctx: GameContext): void {
    const t = TUNING.survival;
    const s = this.stats;

    this.sinceDamage += ctx.dt;
    if (s.health > 0 && s.health < 100 && this.sinceDamage >= t.regenDelay) {
      s.health = clamp(s.health + t.regenRate * ctx.dt, 0, 100);
      this.markChanged();
    }
  }

  private updateSanity(ctx: GameContext): void {
    const s = this.stats;
    const player = ctx.player;
    const room = ctx.render.lightLevelAt(player.pos);
    const seen = effectiveLight(room, player.flashlightOn, player.flashlightCharge);
    const resting = player.crouching && player.speed < 0.2;

    const pressure = Math.max(entityPressure(ctx.entities), this.reportedDread());
    const delta = litRecovery(room, resting) - darknessDrain(seen) - pressure;
    s.sanity = clamp(s.sanity + delta * ctx.dt, 0, 100);

    if (s.sanity < WHISPER_SANITY) {
      this.whisperTimer -= ctx.dt;
      if (this.whisperTimer <= 0) {
        this.events.emit('ui:subtitle', { text: pickWhisper(this.rng, s.sanity), ms: WHISPER_MS });
        const gone = clamp01((WHISPER_SANITY - s.sanity) / WHISPER_SANITY);
        this.whisperTimer = lerp(WHISPER_INTERVAL_HIGH, WHISPER_INTERVAL_LOW, gone) * this.rng.range(0.7, 1.4);
      }
    } else {
      this.whisperTimer = WHISPER_INTERVAL_HIGH;
    }
  }

  /**
   * Worst live rate any entity has reported, expiring stale entries. Worst
   * rather than sum, for the same reason `entityPressure` takes the worst: a
   * pack should be terrifying, not instantly fatal.
   */
  private reportedDread(): number {
    if (this.dread.size === 0) return 0;
    let worst = 0;
    for (const [id, entry] of this.dread) {
      if (this.clock - entry.at > DREAD_TTL) {
        this.dread.delete(id);
        continue;
      }
      if (entry.rate > worst) worst = entry.rate;
    }
    return worst;
  }

  private updateSpeedScale(dt: number, player: PlayerState): void {
    const target = lerp(INJURED_SPEED_SCALE, 1, smoothstep(0, INJURY_CEILING, this.stats.health));
    // Kept locally rather than read back off the player: the controller reads
    // this field, it never writes it, and nothing should fight over it.
    this.speedScale = damp(this.speedScale, target, 4, dt);
    player.speedScale = this.speedScale;
  }

  /**
   * What the player is looking at, and taking it if they asked.
   *
   * Skipped outright while a pointer is aiming. Not merely "does not read
   * `interact`": the camera cone must stop *selecting* too, or it goes on
   * emitting prompts for whatever is at the player's feet over the top of the
   * one the pointer is emitting, and the two take turns owning the HUD.
   */
  private updateFocus(ctx: GameContext): void {
    if (this.aiming) return;
    const grid = this.level?.grid;
    if (!grid) return;
    const found = this.focusFinder.find(ctx.player, grid, TUNING.player.interactRange, this.itemActs, this.extras);
    this.setFocus(found);
    if (found && ctx.input.pressed('interact')) this.interact(found);
  }

  setAiming(on: boolean): void {
    if (on === this.aiming) return;
    this.aiming = on;
    // Whichever selection is being handed over, the incoming one starts from
    // nothing rather than inheriting a target the other found.
    this.setFocus(null);
  }

  setFocusTo(target: Interactable | null): void {
    if (!this.aiming) return;
    this.setFocus(target);
  }

  interactWith(target: Interactable): void {
    this.interact(target);
  }

  repeatPrompt(): void {
    const next = this.focused;
    this.promptFor = next;
    this.events.emit('ui:prompt', { text: next ? `${next.verb} ${next.label}` : null });
  }

  private setFocus(next: Interactable | null): void {
    this.focused = next;
    if (next === this.promptFor) return;
    this.promptFor = next;
    this.events.emit('ui:prompt', { text: next ? `${next.verb} ${next.label}` : null });
  }

  private interact(target: Interactable): void {
    const keep = target.onInteract() === false;
    if (keep) return;
    // Item pickups unhook themselves; this covers ad-hoc interactables that
    // signalled they are spent.
    const i = this.extras.indexOf(target);
    if (i >= 0) this.extras.splice(i, 1);
    if (this.focused === target) this.setFocus(null);
  }

  /* ---------------------------------------------------------------- *
   * World items
   * ---------------------------------------------------------------- */

  private makeItemAct(spot: ItemSpot): Interactable {
    const def = itemDef(spot.kind);
    const xr = TUNING.render.xr;
    return {
      id: spot.id,
      pos: spot.pos,
      radius: TUNING.player.interactRange,
      verb: def.verb,
      label: def.label,
      onInteract: () => this.takeItem(spot),
      // How to point at it, for whoever is aiming with a ray rather than with
      // a crosshair. A supply lies on the floor and is small, and it may be
      // reached for from further off than an arm's length — see `aimReach` in
      // `core/types.ts` for why pointing and reaching are separate numbers.
      aimY: xr.itemAimY,
      aimRadius: xr.itemAimRadius,
      aimReach: xr.interactReach,
    };
  }

  private takeItem(spot: ItemSpot): boolean {
    // A note is a thing to read, not a thing to own: two people on one floor
    // can both read the same page, so it never goes through the claim gate and
    // is never reported to anybody.
    if (spot.kind === 'note') {
      this.readNote(this.noteFor.get(spot.id) ?? 0);
      this.dropWorldItem(spot.id);
      return true;
    }
    // The gate says no, and takes ownership of the decision: the supply stays
    // exactly where it is until it calls back. Returning false is what keeps
    // the interactable registered and the prompt on screen.
    if (this.gate && !this.gate(spot)) return false;
    return this.pocket(spot);
  }

  /** The pickup itself, past the gate. */
  private pocket(spot: ItemSpot): boolean {
    if (!this.addItem(spot.kind, spot.amount)) return false;
    this.dropWorldItem(spot.id);
    return true;
  }

  setPickupGate(gate: ((spot: ItemSpot) => boolean) | null): void {
    this.gate = gate;
  }

  claimItem(id: number): boolean {
    const spot = this.items.find((s) => s.id === id);
    if (!spot) return false;
    if (spot.kind === 'note') {
      this.readNote(this.noteFor.get(spot.id) ?? 0);
      this.dropWorldItem(spot.id);
      return true;
    }
    return this.pocket(spot);
  }

  removeWorldItem(id: number): boolean {
    if (!this.items.some((s) => s.id === id)) return false;
    this.dropWorldItem(id);
    // The crosshair may be resting on the thing that just vanished.
    if (this.focused?.id === id) this.setFocus(null);
    return true;
  }

  /** Removes a spot from the world so the renderer drops its mesh. */
  private dropWorldItem(id: number): void {
    for (let i = 0; i < this.items.length; i++) {
      if (this.items[i]!.id === id) {
        this.items.splice(i, 1);
        break;
      }
    }
    for (let i = 0; i < this.itemActs.length; i++) {
      if (this.itemActs[i]!.id === id) {
        this.itemActs.splice(i, 1);
        break;
      }
    }
  }

  private readNote(index: number): void {
    const note = NOTES[index % NOTES.length]!;
    this.events.emit('ui:note', { id: note.id, title: note.title, body: note.body });
    const gain = itemDef('note').effects.sanity ?? 0;
    if (gain > 0) this.adjust('sanity', gain);
    this.flush(true);
  }

  /* ---------------------------------------------------------------- *
   * Inventory
   * ---------------------------------------------------------------- */

  addItem(kind: ItemKind, amount = 1): boolean {
    // A note is read where it lies rather than carried; slots are for supplies.
    if (kind === 'note') {
      this.readNote(this.rng.int(0, NOTES.length - 1));
      return true;
    }
    // A second flashlight is worth its cells, not a slot.
    if (kind === 'flashlight' && this.inv.count('flashlight') > 0) {
      this.events.emit('flashlight:recharge', { amount: 100 });
      this.events.emit('ui:toast', { text: 'Another flashlight. You take the cells out of it.', tone: 'good' });
      return true;
    }
    if (!this.inv.add(kind, amount)) {
      this.events.emit('item:full', { kind });
      this.events.emit('ui:toast', { text: 'No room for it.', tone: 'bad' });
      return false;
    }
    this.events.emit('item:pickup', { kind, amount });
    this.events.emit('inventory:changed', { slots: this.inv.slots });
    return true;
  }

  consume(kind: ItemKind): boolean {
    if (this.inv.count(kind) <= 0) return false;
    const def = itemDef(kind);

    if (!def.consumable) {
      this.events.emit('ui:toast', { text: def.refusal ?? def.noEffect, tone: 'info' });
      return false;
    }

    if (kind === 'battery') {
      // Without this a battery is silently destroyed into the charge counter of
      // a flashlight the player does not own.
      if (this.inv.count('flashlight') <= 0) {
        this.events.emit('ui:toast', { text: 'Nothing to put it in.', tone: 'info' });
        return false;
      }
      const charge = this.player?.flashlightCharge ?? 0;
      if (charge >= 99) {
        this.events.emit('ui:toast', { text: def.noEffect, tone: 'info' });
        return false;
      }
    } else if (!hasUsefulEffect(def, this.stats)) {
      this.events.emit('ui:toast', { text: def.noEffect, tone: 'info' });
      return false;
    }

    this.inv.remove(kind, 1);

    if (kind === 'battery') {
      this.events.emit('flashlight:recharge', { amount: TUNING.flashlight.batteryCharge });
    } else {
      for (const key of Object.keys(def.effects) as StatKey[]) {
        const delta = def.effects[key];
        if (delta === undefined) continue;
        this.stats[key] = clamp(this.stats[key] + delta, 0, 100);
      }
    }

    this.events.emit('item:consume', { kind });
    this.events.emit('inventory:changed', { slots: this.inv.slots });
    this.markChanged();
    this.flush(true);
    return true;
  }

  consumeSlot(index: number): boolean {
    const kind = this.inv.kindAt(index);
    return kind ? this.consume(kind) : false;
  }

  count(kind: ItemKind): number {
    return this.inv.count(kind);
  }

  /* ---------------------------------------------------------------- *
   * Health
   * ---------------------------------------------------------------- */

  damage(amount: number, source: string): void {
    if (amount <= 0) return;
    this.applyDamage(amount, source);
    this.events.emit('player:hurt', { amount, source: toHurtSource(source) });
    this.markChanged();
    this.flush(true);
  }

  private applyDamage(amount: number, source: string): void {
    this.stats.health = clamp(this.stats.health - amount, 0, 100);
    // Every hit puts healing back to the start of its wait.
    this.sinceDamage = 0;
    if (this.stats.health <= 0 && !this.died) {
      this.died = true;
      this.events.emit('player:died', { cause: source });
    }
  }

  heal(amount: number): void {
    if (amount <= 0) return;
    this.stats.health = clamp(this.stats.health + amount, 0, 100);
    this.markChanged();
    this.flush(true);
  }

  /**
   * Both stats, set rather than nudged.
   *
   * No death check: a snapshot is only ever written for a living player, and a
   * restore is not an injury. `died` goes back to false with them, because the
   * run being put back is one that had not ended.
   */
  setStats(stats: Readonly<SurvivalStats>): void {
    this.stats.health = clamp(stats.health, 0, 100);
    this.stats.sanity = clamp(stats.sanity, 0, 100);
    this.died = this.stats.health <= 0;
    this.belowCritical.clear();
    this.sinceDamage = TUNING.survival.regenDelay;
    this.markChanged();
    this.flush(true);
  }

  /**
   * The pack, put back as it was. Silent by design — see the note on the
   * interface. `flashlight` is carried like anything else here; whether the
   * player can actually *use* one is the player controller's business.
   */
  restoreInventory(slots: readonly InventorySlot[]): void {
    this.inv.clear();
    for (const slot of slots) {
      if (slot.count > 0) this.inv.add(slot.kind, slot.count);
    }
    this.events.emit('inventory:changed', { slots: this.inv.slots });
  }

  adjust(stat: keyof SurvivalStats, delta: number): void {
    if (delta === 0) return;
    this.stats[stat] = clamp(this.stats[stat] + delta, 0, 100);
    if (stat === 'health' && this.stats.health <= 0 && !this.died) {
      this.died = true;
      this.events.emit('player:died', { cause: 'injury' });
    }
    this.markChanged();
  }

  /* ---------------------------------------------------------------- *
   * Ad-hoc interactables
   * ---------------------------------------------------------------- */

  register(interactable: Interactable): () => void {
    this.extras.push(interactable);
    return () => {
      const i = this.extras.indexOf(interactable);
      if (i >= 0) this.extras.splice(i, 1);
      if (this.focused === interactable) this.setFocus(null);
    };
  }

  dispose(): void {
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
  }

  /* ---------------------------------------------------------------- *
   * Stat change reporting
   * ---------------------------------------------------------------- */

  private markChanged(): void {
    this.dirty = true;
  }

  /**
   * `immediate` is for discrete changes the HUD should show at once. The
   * per-frame drain path goes through the throttle instead — nobody needs a
   * sixty-hertz feed of a bar that moves a tenth of a point per second.
   */
  private flush(immediate: boolean): void {
    if (!this.dirty) return;
    if (!immediate) {
      if (this.clock - this.lastEmit < STATS_INTERVAL) return;
      if (!this.moved()) return;
    }
    this.dirty = false;
    this.lastEmit = this.clock;
    const s = this.stats;
    this.sent.health = s.health;
    this.sent.sanity = s.sanity;
    this.events.emit('stats:changed', { stats: s });
  }

  private moved(): boolean {
    for (const key of WATCHED) {
      if (Math.abs(this.stats[key] - this.sent[key]) >= STATS_EPSILON) return true;
    }
    return false;
  }

  private checkCritical(): void {
    const line = TUNING.survival.criticalThreshold;
    for (const key of WATCHED) {
      const v = this.stats[key];
      if (v < line) {
        if (!this.belowCritical.has(key)) {
          this.belowCritical.add(key);
          this.events.emit('stats:critical', { stat: key });
        }
      } else if (v > line + CRITICAL_HYSTERESIS) {
        this.belowCritical.delete(key);
      }
    }
  }
}

/** `player:hurt` has a closed source union; unknown callers report as 'fall'. */
function toHurtSource(source: string): HurtSource {
  return (HURT_SOURCES.has(source) ? source : 'fall') as HurtSource;
}

export function makeSurvivalSystem(events: EventBus): SurvivalSystem {
  return new Survival(events);
}
